import React, { useState, useEffect, useRef } from "react";
import EditableTableRow from "./../general/EditableTableRow";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "./../general/AuthContext";
import KpiTile from "./../general/KpiTile";
import NotesPanel from "./NotesPanel";
import DocumentManager from "./DocumentManager";
import OfficersPanel from "./OfficersPanel";
import AuditLogModal from "./AuditLogModal";
import MembershipAuditModal from "./MembershipAuditModal";
import { canUserEditField, getEditableFields } from "./fieldPermissions";
import { authStorage } from "../../utils/storage";
import { Users, Briefcase, FileText, BarChart3, CheckSquare, DollarSign, Vote, Award, Ban } from 'lucide-react';

const username = authStorage.getUsername();  // Retrieve username from authStorage

const requestBody = {
  // Other data being sent in the request
  username,  // Include the username in the request body
  // other fields...
};

console.log('The user who will be logged is ' + requestBody.username)



const PAYROLL_CONTACT_TYPE_OPTIONS = [
  { value: "sharefile", label: "Sharefile" },
  { value: "send_cards", label: "Send Cards To" },
  { value: "payroll_hr", label: "Payroll/HR (Legacy)" },
];

const createEmptyPayrollContact = () => ({
  contactType: "sharefile",
  contactTitle: "",
  contactName: "",
  contactEmail: "",
  contactPhone: "",
});

const hasPayrollContactValues = (contact = {}) =>
  Boolean(
    contact.contactTitle ||
      contact.contactName ||
      contact.contactEmail ||
      contact.contactPhone
  );

const normalizePayrollContacts = (contacts, legacyContact = null) => {
  const normalized = Array.isArray(contacts)
    ? contacts
        .map((contact) => ({
          contactType: contact?.contactType || "sharefile",
          contactTitle: contact?.contactTitle || "",
          contactName: contact?.contactName || "",
          contactEmail: contact?.contactEmail || "",
          contactPhone: contact?.contactPhone || "",
        }))
        .filter(hasPayrollContactValues)
    : [];

  if (normalized.length > 0) {
    return normalized;
  }

  if (legacyContact && hasPayrollContactValues(legacyContact)) {
    return [
      {
        contactType: "payroll_hr",
        contactTitle: "Payroll/HR Contact",
        contactName: legacyContact.contactName || "",
        contactEmail: legacyContact.contactEmail || "",
        contactPhone: legacyContact.contactPhone || "",
      },
    ];
  }

  return [createEmptyPayrollContact()];
};

function BargainingUnitInfo() {
   // hide legacy officer containers (using new Officers panel)
  const { id } = useParams();
  const navigate = useNavigate(); // Initialize navigate function
  const location = useLocation();
  // Entering from a town scopes prev/next to that town until the user exits.
  const townContext = new URLSearchParams(location.search).get('town');
  // Entering from an employee scopes prev/next to that employee's assigned
  // units the same way (?emp=<employeeId>).
  const empContext = new URLSearchParams(location.search).get('emp');
  const [empScope, setEmpScope] = useState(null); // { name, ids: [unitId, ...] }
  const routeBase = location.pathname.startsWith('/unit-info') ? '/unit-info' : '/units';
  const [linkedUnits, setLinkedUnits] = useState([]);
  const [, setEmployees] = useState([]);
  const [, setTowns] = useState([]);
  const [staffReps, setStaffReps] = useState([]);
  const [fieldReps, setFieldReps] = useState([]);
  const [organizers, setOrganizers] = useState([]);
  const [serviceFieldReps, setServiceFieldReps] = useState([]);
  const [coordinatorDirectory, setCoordinatorDirectory] = useState([]); // assignable coordinators
  const [unitCoordinators, setUnitCoordinators] = useState([]);         // assigned to THIS unit
  const [isCoordinator, setIsCoordinator] = useState(false);           // current user is a coordinator
  const { auth } = useAuth();
  const [bargainingUnits] = useState([]);
  const [, setCurrentIndex] = useState(0);
  const [unitsData, setUnitsData] = useState([]);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [showMembershipAuditModal, setShowMembershipAuditModal] = useState(false);
  const officersPanelRef = useRef(null);
  const [saveStatus, setSaveStatus] = useState(null); // null | 'saving' | 'saved' | 'error'
  const saveTimerRef = useRef(null);

  useEffect(() => {
    // Single fetch on mount and whenever the unit id changes. (An earlier
    // duplicate mount-only effect fetched the same full table back-to-back.)
    // Fetch all units data here and initialize the current index based on the URL
    const fetchData = async () => {
      try {
        const response = await fetch("/api/bargaining-units");
        const data = await response.json();
        setUnitsData(data);
        const initialIndex = data.findIndex(
          (unit) => unit.id.toString() === id
        );
        setCurrentIndex(initialIndex >= 0 ? initialIndex : 0);
      } catch (error) {
        console.error("Failed to fetch unit info:", error);
      }
    };

    fetchData();
  }, [id, navigate]);

  // Who am I as an Employee — lets assignment checks match by ID, so a login
  // name that differs from the Employees record still counts as assigned.
  useEffect(() => {
    (async () => {
      try {
        const token = authStorage.getToken();
        const res = await fetch('/api/me/employee', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setMyEmployee(res.ok ? await res.json() : null);
      } catch { setMyEmployee(null); }
    })();
  }, []);

  useEffect(() => {
    if (!empContext) { setEmpScope(null); return; }
    (async () => {
      try {
        const token = authStorage.getToken();
        const res = await fetch(`/api/employee/${empContext}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(String(res.status));
        const rows = await res.json();
        setEmpScope({
          name: rows[0]?.employee_full_name || 'this employee',
          ids: rows.map((r) => String(r.ID)),
        });
      } catch {
        setEmpScope(null);
      }
    })();
  }, [empContext]);

  // Navigation list: the server returns units ordered by LOCAL then UNIT
  // (chapter-sequential). When a town context is active, prev/next stay
  // inside that town. Derived from the URL each render so it can never drift
  // from the unit actually being shown.
  const navList = empContext && empScope
    ? unitsData.filter((u) => empScope.ids.includes(String(u.id)))
    : townContext
    ? unitsData.filter((u) => String(u.TOWN || '').trim().toLowerCase() === townContext.trim().toLowerCase())
    : unitsData;
  const navIndex = navList.findIndex((u) => String(u.id) === String(id));

  const navToUnit = (unit) => {
    if (!unit || !unit.id) return;
    const suffix = empContext
      ? `?emp=${encodeURIComponent(empContext)}`
      : townContext ? `?town=${encodeURIComponent(townContext)}` : '';
    navigate(`${routeBase}/${unit.id}${suffix}`);
  };

  const goToNextUnit = () => {
    if (!navList.length) return;
    const idx = navIndex < 0 ? 0 : navIndex === navList.length - 1 ? 0 : navIndex + 1;
    navToUnit(navList[idx]);
  };

  const goToPreviousUnit = () => {
    if (!navList.length) return;
    const idx = navIndex < 0 ? 0 : navIndex === 0 ? navList.length - 1 : navIndex - 1;
    navToUnit(navList[idx]);
  };

  // Linked units (shared officer rosters) — shown as a banner when present
  useEffect(() => {
    const fetchLinked = async () => {
      try {
        const token = authStorage.getToken();
        const res = await fetch(`/api/units/${id}/linked`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setLinkedUnits(res.ok ? await res.json() : []);
      } catch {
        setLinkedUnits([]);
      }
    };
    fetchLinked();
  }, [id, navigate]);

  useEffect(() => {
    if (!auth.isAuthenticated) {
      navigate("/login");
    }
  }, [auth, navigate]);




  useEffect(() => {
    const fetchStaffReps = async () => {
      try {
        // Get the authentication token from auth context or sessionStorage
        const token = auth?.token || authStorage.getToken();
        if (!token) {
          console.error("No token found. Redirecting to login.");
          navigate("/login"); // Redirect to login if no token is found
          return;
        }

        // Fetch staff reps data with token in headers
        const response = await fetch('/api/staff-reps', {
          headers: {
            Authorization: `Bearer ${token}`, // Include the token in the header
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setStaffReps(Array.isArray(data) ? data : []); // Ensure data is an array
      } catch (error) {
        console.error("Error fetching staff reps:", error);
        setStaffReps([]); // Set an empty array on error
      }
    };

    fetchStaffReps();
  }, [auth.token, navigate]); // Add dependencies to ensure proper token use

  useEffect(() => {
    fetch('/api/field-reps', {
      headers: {
        Authorization: `Bearer ${auth.token}`,
      },
    })
      .then(response => response.json())
      .then(data => {
        console.log('Field Reps API Response:', data);
        setFieldReps(Array.isArray(data) ? data : []);
      })
      .catch(error => {
        console.error('Error fetching field reps:', error);
        setFieldReps([]);
      });
  }, [auth.token]);

  useEffect(() => {
    fetch("/api/organizers", {
      headers: {
        Authorization: `Bearer ${auth.token}`, // Include token if required
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setOrganizers(data);
        } else {
          console.error("Unexpected data format for organizers:", data);
          setOrganizers([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching organizers:", error);
        setOrganizers([]);
      });
  }, [auth.token]);

  useEffect(() => {
    fetch("/api/service-reps", {
      headers: {
        Authorization: `Bearer ${auth.token}`,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setServiceFieldReps(data);
        } else {
          console.error("Unexpected data format for service reps:", data);
          setServiceFieldReps([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching service reps:", error);
        setServiceFieldReps([]);
      });
  }, [auth.token]);

  useEffect(() => {
    fetch("/api/allemployees", {
      headers: {
        Authorization: `Bearer ${auth.token}`,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setEmployees(data);
        } else {
          console.error("Unexpected data format for employees:", data);
          setEmployees([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching employees:", error);
        setEmployees([]);
      });
  }, [auth.token]);

  useEffect(() => {
    fetch("/api/towns", {
      headers: {
        Authorization: `Bearer ${auth.token}`,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setTowns(data);
        } else {
          console.error("Unexpected data format for towns:", data);
          setTowns([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching towns:", error);
        setTowns([]);
      });
  }, [auth.token]);

  const [isEditMode, setIsEditMode] = useState(false);
  useState('membership');
  useState(['membership']); // Start with first section expanded
  const [raises, setRaises] = useState([]);
  const [raiseForm, setRaiseForm] = useState({ effective_date: '', raise_type: 'GWI', raise_pct: '', raise_amt: '', notes: '' });
  const [, setRaiseFormOpen] = useState(false);
  const [raiseError, setRaiseError] = useState('');
  const [raiseSaving, setRaiseSaving] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [userName, setUserName] = useState('');
  const [userFullName, setUserFullName] = useState('');
  const [assignedStaffRep, setAssignedStaffRep] = useState('');
  const [assignedUsers, setAssignedUsers] = useState({});
  const [myEmployee, setMyEmployee] = useState(null); // { employeeId, employeeFullName }
  const [editableFields, setEditableFields] = useState([]);
  const [editData, setEditData] = useState({
    RepAssigned1: "",
    RepAssigned2: "",
    RepAssigned1_Id: "",
    RepAssigned2_Id: "",
    ServiceFieldRep1: "",
    ServiceFieldRep2: "",
    ServiceFieldRep1_Id: "",
    ServiceFieldRep2_Id: "",
    FieldRep1: "",
    FieldRep1_Id: "",
    FieldRep2: "",
    FieldRep2_Id: "",
    Organizer: "",
    Organizer_Id: "",
    LOCAL: "",
    UNIT: "",
    BARGAINING_UNIT: "",
    TOWN: "",
    CBA_START: "",
    CBA_EXP: "",
    PRESIDENT_FIRST: "",
    PRESIDENT_LAST: "",
    PRESIDENT_EMAIL: "",
    PRESIDENT_PRIMARY_PHONE: "",
    PRESIDENT_SECONDARY_PHONE: "",
    VICE_PRESIDENT_FIRST: "",
    VICE_PRESIDENT_LAST: "",
    VICE_PRESIDENT_EMAIL: "",
    VICE_PRESIDENT_PRIMARY_PHONE: "",
    VICE_PRESIDENT_SECONDARY_PHONE: "",
    TREASURER_FIRST: "",
    TREASURER_LAST: "",
    TREASURER_EMAIL: "",
    TREASURER_PRIMARY_PHONE: "",
    TREASURER_SECONDARY_PHONE: "",
    SECRETARY_FIRST: "",
    SECRETARY_LAST: "",
    SECRETARY_EMAIL: "",
    SECRETARY_PRIMARY_PHONE: "",
    SECRETARY_SECONDARY_PHONE: "",
    payrollContacts: [createEmptyPayrollContact()],
    PayrollHRContactName: "",
    PayrollHRContactEmail: "",
    PayrollHRContactNumber: "",
    NewRecommendedContractLanguage: "",
    AreOrientationsHappening: "",
    WhoDoesOrientation: "",
    ActiveJobCount: "",
    TotalDuesPayers: "",
    OnCentralizedDues: "",
    DuesMonthApplied: "",
    DuesFrequency: "",
    NumberOfHomeEmails: "",
    NumberOfPhoneNumbers: "",
    DateLastBargUnitListReceived: "",
    NoCardNoDues: "",
    DuesNoCard: "",
    Refusals: "",
    CardNoDues: "",
    DuesWithCard: "",
    AuditCompleteOverview: "",
    DateAudited: "",
    PEOPLEContributors: "",
    TotalOPTOUTS: "",
    RecommitsSaves: "",
    ein: "",
    DateLastOfficerUpdate: "",
    LastElection: "",
    IUMonthYearNextElection: "",
    LengthOfTerm: "",
    PresidentFIRST: "",
    PresidentLast: "",
    PresidentContactNumber: "",
    PresidentEmail: "",
    Notes: "",
    // Add other fields as necessary
  });

  const [BargainingUnitInfoData, BargainingUnitInfoSetData] = useState({
    membershipInformation: {},
    payrollInformation: {},
    contractsAndNegotiations: {},
    membershipMetrics: {},
    auditsAndCompliance: {},
    financialMetrics: {},
    electionAndOfficerInformation: {},
    additionalNotes: {},
    localPresident: {},
    localVicePresident: {},
    localTreasurer: {},
    localSecretary: {}

  });

  // Initialize user role and permissions
  useEffect(() => {
    if (auth && auth.user) {
      setUserRole(auth.user.role || authStorage.getRole() || '');
      setUserName(auth.user.username || authStorage.getUsername() || '');
      setUserFullName(auth.user.fullName || authStorage.getFullName() || '');
    } else {
      // Fallback to sessionStorage if auth context not available
      setUserRole(authStorage.getRole() || '');
      setUserName(authStorage.getUsername() || '');
      setUserFullName(authStorage.getFullName() || '');
    }
  }, [auth]);

  // Update editable fields when user data or assigned users change
  // Authoritative "am I assigned to this unit" — matches by EmployeeId first
  // (via /api/me/employee), then by the Employees record's canonical name.
  // Login-name matching alone broke for reps whose Users name differs from
  // their Employees name (Lorin Dafoe vs Lorin Raccuia).
  const myAssignedById = (() => {
    if (!myEmployee) return false;
    const myId = myEmployee.employeeId != null ? Number(myEmployee.employeeId) : null;
    const ids = [
      editData.RepAssigned1_Id, editData.RepAssigned2_Id,
      editData.ServiceFieldRep1_Id, editData.FieldRep1_Id,
      editData.FieldRep2_Id, editData.Organizer_Id,
    ].filter((v) => v !== '' && v != null).map(Number);
    if (myId != null && ids.includes(myId)) return true;
    const empName = String(myEmployee.employeeFullName || '').trim();
    return !!empName && Object.values(assignedUsers).some(
      (n) => n && String(n).trim() === empName
    );
  })();

  useEffect(() => {
    if (userRole && userName && Object.keys(assignedUsers).length > 0) {
      const fields = getEditableFields(userRole, userName, assignedStaffRep, userFullName, assignedUsers, isCoordinator, myAssignedById);
      setEditableFields(fields);
    }
  }, [userRole, userName, userFullName, assignedStaffRep, assignedUsers, isCoordinator, myAssignedById]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = authStorage.getToken();
        if (!token) {
          console.error("No token found. Redirecting to login.");
          return navigate("/login");
        }

        const response = await fetch(`/api/unit-info/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            console.error("Unauthorized access. Redirecting to login.");
            authStorage.clearAuthData();
            return navigate("/login");
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Map bu_ prefixed fields to plain field names
        const mappedData = {
          ...data,
          // Map president fields
          PRESIDENT_FIRST: data.bu_PRESIDENT_FIRST || data.PRESIDENT_FIRST || '',
          PRESIDENT_LAST: data.bu_PRESIDENT_LAST || data.PRESIDENT_LAST || '',
          PRESIDENT_EMAIL: data.bu_PRESIDENT_EMAIL || data.PRESIDENT_EMAIL || '',
          PRESIDENT_PRIMARY_PHONE: data.bu_PRESIDENT_PRIMARY_PHONE || data.PRESIDENT_PRIMARY_PHONE || '',
          PRESIDENT_SECONDARY_PHONE: data.bu_PRESIDENT_SECONDARY_PHONE || data.PRESIDENT_SECONDARY_PHONE || '',
          // Map vice president fields
          VICE_PRESIDENT_FIRST: data.bu_VICE_PRESIDENT_FIRST || data.VICE_PRESIDENT_FIRST || '',
          VICE_PRESIDENT_LAST: data.bu_VICE_PRESIDENT_LAST || data.VICE_PRESIDENT_LAST || '',
          VICE_PRESIDENT_EMAIL: data.bu_VICE_PRESIDENT_EMAIL || data.VICE_PRESIDENT_EMAIL || '',
          VICE_PRESIDENT_PRIMARY_PHONE: data.bu_VICE_PRESIDENT_PRIMARY_PHONE || data.VICE_PRESIDENT_PRIMARY_PHONE || '',
          VICE_PRESIDENT_SECONDARY_PHONE: data.bu_VICE_PRESIDENT_SECONDARY_PHONE || data.VICE_PRESIDENT_SECONDARY_PHONE || '',
          // Map secretary fields
          SECRETARY_FIRST: data.bu_SECRETARY_FIRST || data.SECRETARY_FIRST || '',
          SECRETARY_LAST: data.bu_SECRETARY_LAST || data.SECRETARY_LAST || '',
          SECRETARY_EMAIL: data.bu_SECRETARY_EMAIL || data.SECRETARY_EMAIL || '',
          SECRETARY_PRIMARY_PHONE: data.bu_SECRETARY_PRIMARY_PHONE || data.SECRETARY_PRIMARY_PHONE || '',
          SECRETARY_SECONDARY_PHONE: data.bu_SECRETARY_SECONDARY_PHONE || data.SECRETARY_SECONDARY_PHONE || '',
          // Map treasurer fields
          TREASURER_FIRST: data.bu_TREASURER_FIRST || data.TREASURER_FIRST || '',
          TREASURER_LAST: data.bu_TREASURER_LAST || data.TREASURER_LAST || '',
          TREASURER_EMAIL: data.bu_TREASURER_EMAIL || data.TREASURER_EMAIL || '',
          TREASURER_PRIMARY_PHONE: data.bu_TREASURER_PRIMARY_PHONE || data.TREASURER_PRIMARY_PHONE || '',
          TREASURER_SECONDARY_PHONE: data.bu_TREASURER_SECONDARY_PHONE || data.TREASURER_SECONDARY_PHONE || '',
        };

        const legacyPayrollContact = {
          contactName: mappedData.PayrollHRContactName || "",
          contactEmail: mappedData.PayrollHRContactEmail || "",
          contactPhone: mappedData.PayrollHRContactNumber || "",
        };

        // Validate fields before setting data
        const validatedData = {
          ...mappedData,
          CBA_START: mappedData.CBA_START || null,
          CBA_EXP: mappedData.CBA_EXP || null,
          NoCardNoDues: mappedData.NoCardNoDues ?? "",
          DuesNoCard: mappedData.DuesNoCard ?? "",
          TotalOPTOUTS: mappedData.TotalOPTOUTS ?? "",
          Refusals: mappedData.Refusals ?? "",
          CardNoDues: mappedData.CardNoDues ?? "",
          DuesWithCard: mappedData.DuesWithCard ?? "",
          payrollContacts: normalizePayrollContacts(
            mappedData.payrollContacts,
            legacyPayrollContact
          ),
        };

        BargainingUnitInfoSetData(validatedData);
        setEditData(validatedData);
        // Set the assigned staff rep for permission checking
        setAssignedStaffRep(validatedData.RepAssigned1 || '');

        // Set all assigned users for permission checking
        setAssignedUsers({
          staffRep1: validatedData.RepAssigned1 || '',
          staffRep2: validatedData.RepAssigned2 || '',
          serviceRep: validatedData.ServiceFieldRep1 || '',
          fieldRep: validatedData.FieldRep1 || '',
          fieldRep2: validatedData.FieldRep2 || '',
          organizer: validatedData.Organizer || ''
        });

        console.log("Data fetched successfully:", validatedData);
      } catch (error) {
        console.error("Error fetching unit info:", error);
      }
    };

    fetchData();
  }, [id, navigate]);

  useEffect(() => {
    const token = authStorage.getToken();
    if (!token || !id) return;
    fetch(`/api/units/${id}/raises`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => setRaises(Array.isArray(data) ? data : []))
      .catch(() => setRaises([]));
  }, [id, navigate]);

  // A DATE column arrives as a UTC-midnight ISO timestamp; new Date() +
  // toLocaleDateString() re-renders it in local time, showing the PREVIOUS
  // day in Eastern (the GWI "date off-set" bug). Format from the date part
  // of the string only — no timezone math.
  const rawDatePart = (v) => {
    const m = String(v || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? { y: m[1], mo: m[2], d: m[3] } : null;
  };
  const fmtRaiseDate = (v) => {
    const p = rawDatePart(v);
    if (p) return `${Number(p.mo)}/${Number(p.d)}/${p.y}`;
    return v ? new Date(v).toLocaleDateString() : '—';
  };
  const raiseYear = (v) => {
    const p = rawDatePart(v);
    if (p) return p.y;
    return v ? new Date(v).getFullYear() : 'Unknown';
  };

  const handleRaiseSubmit = async (e) => {
    e.preventDefault();
    setRaiseSaving(true);
    setRaiseError('');
    const token = authStorage.getToken();
    try {
      const res = await fetch(`/api/units/${id}/raises`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(raiseForm),
      });
      if (!res.ok) throw new Error('Save failed');
      const { id: newId } = await res.json();
      setRaises(prev => [{ ...raiseForm, id: newId, unit_id: id, created_by: authStorage.getUsername(), created_at: new Date().toISOString() }, ...prev]);
      setRaiseForm({ effective_date: '', raise_type: 'GWI', raise_pct: '', raise_amt: '', notes: '' });
      setRaiseFormOpen(false);
    } catch (err) {
      // Never fail silently — the missing-table bug looked exactly like "nothing happened".
      setRaiseError('Could not save this raise. Please try again, or contact an administrator if it keeps failing.');
    } finally {
      setRaiseSaving(false);
    }
  };

  const handleRaiseDelete = async (raiseId) => {
    if (!window.confirm('Delete this raise entry?')) return;
    const token = authStorage.getToken();
    await fetch(`/api/raises/${raiseId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    setRaises(prev => prev.filter(r => r.id !== raiseId));
  };

  const handleEditToggle = async () => {
    // Enter edit mode
    if (!isEditMode) {
      setIsEditMode(true);
      return;
    }

    // Cancel edit mode: discard unsaved unit form edits
    setEditData({ ...BargainingUnitInfoData });
    setRaiseForm({ effective_date: '', raise_type: 'GWI', raise_pct: '', raise_amt: '', notes: '' });

    // Cancel edit mode: discard unsaved officer panel edits
    if (officersPanelRef.current && typeof officersPanelRef.current.discardChanges === "function") {
      try {
        const reverted = await officersPanelRef.current.discardChanges();
        if (!reverted) {
          alert("Unable to fully discard officer changes right now. Please try again.");
          return;
        }
      } catch (e) {
        console.error("Error discarding officer changes:", e);
        alert("Unable to fully discard officer changes right now. Please try again.");
        return;
      }
    }

    setIsEditMode(false);
  };

  // Helper function to check if current user can edit a specific field
  const canEditField = (fieldName) => {
    return canUserEditField(fieldName, userRole, userName, assignedStaffRep, userFullName, assignedUsers, isCoordinator, myAssignedById);
  };

  const isAdminUser = ['admin', 'manager', 'maintainer'].includes(String(userRole || '').toLowerCase());

  // Load the coordinator directory once, and flag whether the current user is one.
  useEffect(() => {
    const loadCoordinators = async () => {
      try {
        const token = authStorage.getToken();
        const res = await fetch('/api/coordinators', { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const dir = await res.json();
        setCoordinatorDirectory(Array.isArray(dir) ? dir : []);
        if (userName) setIsCoordinator((dir || []).some((c) => c.username === userName));
      } catch (e) { /* directory optional */ }
    };
    loadCoordinators();
  }, [userName]);

  // Load THIS unit's assigned coordinators whenever the unit changes.
  const fetchUnitCoordinators = async (unitId) => {
    if (!unitId) return;
    try {
      const token = authStorage.getToken();
      const res = await fetch(`/api/units/${unitId}/coordinators`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const rows = await res.json();
      setUnitCoordinators(Array.isArray(rows) ? rows : []);
    } catch (e) { setUnitCoordinators([]); }
  };
  useEffect(() => { fetchUnitCoordinators(id); }, [id, navigate]);

  const assignCoordinator = async (employeeId) => {
    if (!employeeId) return;
    try {
      const token = authStorage.getToken();
      const res = await fetch(`/api/units/${id}/coordinators`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ EmployeeId: employeeId }),
      });
      if (res.ok) fetchUnitCoordinators(id);
    } catch (e) { /* ignore */ }
  };

  const unassignCoordinator = async (employeeId) => {
    try {
      const token = authStorage.getToken();
      const res = await fetch(`/api/units/${id}/coordinators/${employeeId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) fetchUnitCoordinators(id);
    } catch (e) { /* ignore */ }
  };

  const handleInputChange = (e) => {
    const { name, value, idKey, idValue } = e.target;
    console.log(
      `Handling change for ${name}, ID Key: ${idKey}, ID Value: ${idValue}`
    );
    setEditData((prevData) => {
      const newData = {
        ...prevData,
        [name]: value,
        ...(idKey && { [idKey]: idValue }),
      };
      // Total Dues Payers is a hand-maintained metric (like Active Job Count),
      // but as a convenience we AUTO-FILL it from the two dues-paying card-audit
      // categories WHEN IT IS BLANK. Once a value exists — entered by hand or
      // previously stored — we never overwrite it; the team edits it directly.
      if (name === 'DuesNoCard' || name === 'DuesWithCard') {
        const toNum = (v) => {
          if (v === null || v === undefined || String(v).trim() === '') return null;
          const n = Number(v);
          return Number.isFinite(n) ? n : null;
        };
        const totalIsBlank = String(newData.TotalDuesPayers ?? '').trim() === '';
        const duesNoCard = toNum(newData.DuesNoCard);
        const duesWithCard = toNum(newData.DuesWithCard);
        if (totalIsBlank && (duesNoCard !== null || duesWithCard !== null)) {
          newData.TotalDuesPayers = String((duesNoCard || 0) + (duesWithCard || 0));
        }
      }
      console.log("New editData:", newData);
      return newData;
    });
  };

  const handlePayrollContactChange = (index, field, value) => {
    setEditData((prevData) => {
      const nextContacts = Array.isArray(prevData.payrollContacts)
        ? [...prevData.payrollContacts]
        : [createEmptyPayrollContact()];
      nextContacts[index] = {
        ...createEmptyPayrollContact(),
        ...(nextContacts[index] || {}),
        [field]: value,
      };
      return {
        ...prevData,
        payrollContacts: nextContacts,
      };
    });
  };

  const handleAddPayrollContact = () => {
    setEditData((prevData) => ({
      ...prevData,
      payrollContacts: [
        ...(Array.isArray(prevData.payrollContacts)
          ? prevData.payrollContacts
          : []),
        createEmptyPayrollContact(),
      ],
    }));
  };

  const handleRemovePayrollContact = (index) => {
    setEditData((prevData) => {
      const existing = Array.isArray(prevData.payrollContacts)
        ? prevData.payrollContacts
        : [];
      const remaining = existing.filter((_, itemIndex) => itemIndex !== index);
      return {
        ...prevData,
        payrollContacts:
          remaining.length > 0 ? remaining : [createEmptyPayrollContact()],
      };
    });
  };

  // Persist the edit (officers first, then the unit PUT). Resolves true, or throws on failure.
  const persistUnit = async (officersPromise) => {
    const token = authStorage.getToken();
    const username = authStorage.getUsername();
    if (!token) { navigate("/login"); return false; }

    const officersOk = await officersPromise;
    if (officersOk === false) throw new Error("Officer changes could not be saved");

    const validPayrollContacts = normalizePayrollContacts(editData.payrollContacts);
    const validData = { ...editData, payrollContacts: validPayrollContacts, updatedBy: username };
    if (!validData.id) delete validData.id;
    if (!validData.bargainingUnitId) delete validData.bargainingUnitId;
    if (!validData.UserId) delete validData.UserId;

    const response = await fetch(`/api/unit-info/${id}`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify(validData),
    });
    if (!response.ok) throw new Error("Failed to save data");
    return true;
  };

  // Fire the save in the background and drive the inline status chip (no blocking, no popups).
  const runSave = (officersPromise) => {
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
    setSaveStatus("saving");
    persistUnit(officersPromise)
      .then(() => {
        setSaveStatus("saved");
        saveTimerRef.current = setTimeout(() => setSaveStatus(null), 2500);
      })
      .catch((error) => {
        console.error("Error saving unit info:", error);
        setSaveStatus("error"); // surfaced inline in the header, not as an alert
      });
  };

  const handleSave = () => {
    if (!authStorage.getToken()) {
      console.error("No token found. Redirecting to login.");
      return navigate("/login");
    }
    // Kick off the officers save while its panel is still mounted (before we leave edit mode).
    const panel = (isEditMode && officersPanelRef.current) ? officersPanelRef.current : null;
    const officersPromise = panel
      ? Promise.resolve().then(() => panel.saveAll()).catch((e) => { console.error("Error saving officers data:", e); return false; })
      : Promise.resolve(true);
    // Optimistic UI: apply the edit + leave edit mode immediately; persistence happens in the background.
    BargainingUnitInfoSetData(editData);
    setIsEditMode(false);
    runSave(officersPromise);
  };

  // Retry re-sends the current edit (the officers panel is gone in view mode, so just the unit PUT).
  const handleRetrySave = () => runSave(Promise.resolve(true));


  // Mobile section configuration


  // Toggle section expansion for mobile accordion


  // Helper function to render sections conditionally


  // Check if mobile
  const [, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const payrollContacts =
    Array.isArray(editData.payrollContacts) && editData.payrollContacts.length > 0
      ? editData.payrollContacts
      : [createEmptyPayrollContact()];
  const canEditPayrollContacts = canEditField('PayrollHRContactName');

  const prevUnit = navList.length > 0 ? navList[navIndex <= 0 ? navList.length - 1 : navIndex - 1] : null;
  const nextUnit = navList.length > 0 ? navList[navIndex < 0 || navIndex === navList.length - 1 ? 0 : navIndex + 1] : null;

  return (
    <div className="unit-detail-page">
      <style>{`
        /* ------------------------------------------------------------------
           Mobile rules for the Unit Detail page (scoped to .unit-detail-page).
           Mirrored in src/components/styles/units/Unit.css. datahouse.css
           already cards table.tbl and wraps .list-toolbar at max-width:900px;
           these rules stack the KPI/info grids, tidy the label/value tables,
           and de-clutter the nav toolbar. Desktop (>900px) is unaffected.
           ------------------------------------------------------------------ */
        @media (max-width: 768px) {
          /* KPI tiles + info-card rows stack full-width so values never clip */
          .unit-detail-page .grid-5,
          .unit-detail-page .grid-4,
          .unit-detail-page .grid-3 { grid-template-columns: 1fr; }
          /* Prev/next unit-name hints crowd the toolbar on a phone */
          .unit-detail-page .unit-nav-hint { display: none; }
          /* Nav/action toolbar wraps cleanly instead of forcing one rigid row */
          .unit-detail-page .unit-nav-toolbar { gap: 8px; row-gap: 8px; }
          .unit-detail-page .unit-nav-toolbar > .nav-spacer { display: none; }
          .unit-detail-page .unit-nav-toolbar .nav-actions { margin-left: auto; }
        }
        @media (max-width: 900px) {
          /* Label/value info tables render as clean stacked rows inside their
             card (overrides datahouse's per-row card border for these). */
          .unit-detail-page table.tbl.kv tr {
            display: block; margin: 0; padding: 9px 0;
            border: none; border-bottom: 1px solid var(--border-subtle);
            border-radius: 0; background: transparent;
          }
          .unit-detail-page table.tbl.kv tr:last-child { border-bottom: none; }
          .unit-detail-page table.tbl.kv td {
            display: block; text-align: left !important; padding: 0 !important;
            white-space: normal !important; max-width: none !important; overflow: visible !important;
          }
          .unit-detail-page table.tbl.kv td::before { content: none !important; }
          .unit-detail-page table.tbl.kv td:first-child {
            font-size: 11px; font-weight: 600; letter-spacing: .04em;
            text-transform: uppercase; color: var(--fg-3); margin-bottom: 2px;
          }
          .unit-detail-page table.tbl.kv td:nth-child(2) {
            font-size: 15px; color: var(--fg-1); text-align: left !important;
          }
          /* Edit-mode controls fill the row and never overflow */
          .unit-detail-page table.tbl.kv td input,
          .unit-detail-page table.tbl.kv td select,
          .unit-detail-page table.tbl.kv td .react-datepicker-wrapper,
          .unit-detail-page table.tbl.kv td .datePicker {
            width: 100%; max-width: 100%; box-sizing: border-box;
          }
        }
      `}</style>


      {/* Nav bar */}
      <div className="card" style={{ marginBottom: 16, position: 'sticky', top: 'var(--topbar-h, 60px)', zIndex: 50 }}>
        <div className="list-toolbar unit-nav-toolbar" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="btn btn-secondary btn-sm" onClick={goToPreviousUnit} disabled={!navList || navList.length <= 1}>← Prev</button>
            {prevUnit && <span className="unit-nav-hint" style={{ fontSize: 12.5, color: 'var(--fg-3)', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{prevUnit.NAME}</span>}
          </div>
          <div className="nav-spacer" style={{ flex: 1 }} />
          <div className="nav-actions" style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowAuditLog(true)}>Audit Log</button>
            <button
              className="btn btn-secondary btn-sm"
              onClick={editableFields.length === 0 && !isEditMode ? undefined : handleEditToggle}
              disabled={editableFields.length === 0 && !isEditMode}
            >
              {isEditMode ? 'Cancel' : 'Edit'}
            </button>
            {isEditMode && <button className="btn btn-primary btn-sm" onClick={handleSave}>Save</button>}
            {saveStatus === "saving" && (
              <span aria-live="polite" style={{ display: "inline-flex", alignItems: "center", fontSize: 13, color: "var(--fg-2)" }}>Saving…</span>
            )}
            {saveStatus === "saved" && (
              <span aria-live="polite" style={{ display: "inline-flex", alignItems: "center", fontSize: 13, fontWeight: 600, color: "var(--color-success, #16a34a)" }}>Saved ✓</span>
            )}
            {saveStatus === "error" && (
              <span aria-live="polite" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: "var(--color-danger, #dc2626)" }}>
                Couldn't save changes
                <button className="btn btn-secondary btn-sm" style={{ padding: "2px 10px" }} onClick={handleRetrySave}>Retry</button>
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {nextUnit && <span className="unit-nav-hint" style={{ fontSize: 12.5, color: 'var(--fg-3)', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nextUnit.NAME}</span>}
            <button className="btn btn-secondary btn-sm" onClick={goToNextUnit} disabled={!navList || navList.length <= 1}>Next →</button>
          </div>
        </div>
      </div>

      {/* Employee browsing context — prev/next stay inside their assigned units */}
      {empContext && empScope && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          padding: '9px 14px', marginBottom: 12, borderRadius: 8,
          background: 'var(--bg-accent-soft, #eef4ff)', border: '1px solid var(--accent, #2563eb)',
          fontSize: 13.5,
        }}>
          <span style={{ fontWeight: 700, color: 'var(--accent, #2563eb)' }}>
            Browsing {empScope.name}&rsquo;s units
          </span>
          <span style={{ color: 'var(--fg-2)' }}>
            {navIndex >= 0 ? `unit ${navIndex + 1} of ${navList.length}` : `${navList.length} units`} assigned — Prev/Next stay within their assignments
          </span>
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/employee/${encodeURIComponent(empContext)}`)}>
              ← All their units
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate(`${routeBase}/${id}`, { replace: true })}>
              Exit employee view
            </button>
          </span>
        </div>
      )}

      {/* Town browsing context — prev/next stay inside this town until exit */}
      {townContext && !empContext && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          padding: '9px 14px', marginBottom: 12, borderRadius: 8,
          background: 'var(--bg-accent-soft, #eef4ff)', border: '1px solid var(--accent, #2563eb)',
          fontSize: 13.5,
        }}>
          <span style={{ fontWeight: 700, color: 'var(--accent, #2563eb)' }}>
            Browsing {townContext}
          </span>
          <span style={{ color: 'var(--fg-2)' }}>
            {navIndex >= 0 ? `unit ${navIndex + 1} of ${navList.length}` : `${navList.length} units`} in this town — Prev/Next stay in {townContext}
          </span>
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/town/${encodeURIComponent(townContext)}`)}>
              ← All {townContext} units
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate(`${routeBase}/${id}`, { replace: true })}>
              Exit town view
            </button>
          </span>
        </div>
      )}

      {/* Linked units — officers are shared across these units */}
      {linkedUnits.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          padding: '9px 14px', marginBottom: 12, borderRadius: 8,
          background: '#f3eefe', border: '1px solid #7c5cd6', fontSize: 13.5,
        }}>
          <span style={{ fontWeight: 700, color: '#5b3fb5' }}>Linked unit</span>
          <span style={{ color: 'var(--fg-2)' }}>
            Officers are shared with{linkedUnits.length > 1 ? ':' : ''}
          </span>
          {linkedUnits.map((lu) => (
            <button
              key={lu.id}
              className="btn btn-secondary btn-sm"
              onClick={() => navigate(`${routeBase}/${lu.id}`)}
              title="Open linked unit"
              style={{ fontFamily: 'var(--font-mono, monospace)' }}
            >
              {String(lu.LOCAL || '').padStart(4, '0')}-{String(lu.UNIT || 0).padStart(4, '0')} · {lu.NAME}
            </button>
          ))}
          <span style={{ color: 'var(--fg-3)', fontSize: 12 }}>
            Adding, reassigning, or removing an officer here updates the linked unit too.
          </span>
        </div>
      )}

      {/* Title */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0, letterSpacing: '-0.01em' }}>
          <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent)' }}>
            {editData.LOCAL || 'N/A'}-{editData.UNIT ? String(editData.UNIT).padStart(4, '0') : '0000'}
          </span>
          {' — '}
          {editData.BARGAINING_UNIT || editData.NAME || 'N/A'}
        </h1>
        <div style={{ fontSize: 13.5, color: 'var(--fg-3)', marginTop: 4 }}>
          {editData.TOWN && <span>{editData.TOWN}</span>}
          {editData.TOWN && editData.TYPE && <span> · </span>}
          {editData.TYPE && <span>{editData.TYPE}</span>}
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid-5" style={{ marginBottom: 20 }}>
        <KpiTile icon={Briefcase} label="Active Jobs" value={editData.ActiveJobCount ?? '—'} />
        <KpiTile icon={Users} label="Dues Payers" value={editData.TotalDuesPayers ?? '—'} />
        <KpiTile icon={CheckSquare} label="Opt Outs" value={editData.TotalOPTOUTS ?? '—'} />
        <KpiTile icon={Ban} label="Refusals" value={editData.Refusals ?? '—'} />
        <KpiTile icon={DollarSign} label="PEOPLE" value={editData.PEOPLEContributors ?? '—'} />
      </div>

      {/* Row 1: Membership | Payroll | Contract */}
      <div className="grid-3" style={{ marginBottom: 20 }}>
        <div className="card">
          <div className="card-head"><Users size={15} style={{ color: 'var(--fg-3)' }} /><h3>Membership Info</h3></div>
          <div style={{ padding: 0 }}>
            <table className="tbl kv">
              <tbody>
                <EditableTableRow label="Staff Rep" value={editData.RepAssigned1 || ""} name="RepAssigned1" onChange={handleInputChange} isEditMode={isEditMode} isDropdown={true} options={Array.isArray(staffReps) ? staffReps.map(rep => ({ id: rep.EmployeeId, employee_full_name: rep.full_name })) : []} hiddenData={{ hiddenId1: editData.RepAssigned1_Id }} disabled={!canEditField('RepAssigned1')} />
                <EditableTableRow label="Service Rep" value={editData.ServiceFieldRep1 || ""} name="ServiceFieldRep1" onChange={handleInputChange} isEditMode={isEditMode} isDropdown={true} options={serviceFieldReps.map(rep => ({ id: rep.EmployeeId, employee_full_name: rep.full_name }))} hiddenData={{ hiddenId2: editData.ServiceFieldRep1_Id }} disabled={!canEditField('ServiceFieldRep1')} />
                <EditableTableRow label="Field Rep" value={editData.FieldRep1 || ""} name="FieldRep1" onChange={handleInputChange} isEditMode={isEditMode} isDropdown={true} options={fieldReps.map(rep => ({ id: rep.EmployeeId, employee_full_name: rep.full_name }))} hiddenData={{ hiddenId3: editData.FieldRep1_Id }} disabled={!canEditField('FieldRep1')} />
                {/* Second field rep. EditableTableRow derives the id field from
                    name ("FieldRep2" -> "FieldRep2_Id"), so no hidden id prop. */}
                <EditableTableRow label="Field Rep 2" value={editData.FieldRep2 || ""} name="FieldRep2" onChange={handleInputChange} isEditMode={isEditMode} isDropdown={true} options={fieldReps.map(rep => ({ id: rep.EmployeeId, employee_full_name: rep.full_name }))} disabled={!canEditField('FieldRep2')} />
                <EditableTableRow label="Organizer" value={editData.Organizer || ""} name="Organizer" onChange={handleInputChange} isEditMode={isEditMode} isDropdown={true} options={organizers.map(rep => ({ id: rep.EmployeeId, employee_full_name: rep.full_name }))} hiddenData={{ hiddenId4: editData.Organizer_Id }} disabled={!canEditField('Organizer')} />
                <EditableTableRow label="Staff Rep 2" value={editData.RepAssigned2 || ""} name="RepAssigned2" onChange={handleInputChange} isEditMode={isEditMode} isDropdown={true} options={staffReps.map(rep => ({ id: rep.EmployeeId, employee_full_name: rep.full_name }))} hiddenData={{ hiddenId5: editData.RepAssigned2_Id }} disabled={!canEditField('RepAssigned2')} />
                <tr>
                  <td style={{ fontWeight: 500, color: 'var(--fg-2)', whiteSpace: 'nowrap', verticalAlign: 'top', paddingTop: 10 }}>Coordinator(s)</td>
                  <td>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                      {unitCoordinators.length === 0 && (
                        <span style={{ color: 'var(--fg-3)', fontSize: 13 }}>None assigned</span>
                      )}
                      {unitCoordinators.map((c) => (
                        <span key={c.EmployeeId} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 9px', borderRadius: 999, background: 'var(--color-success-soft, #dcfce7)', color: '#166534', fontSize: 12, fontWeight: 600 }}>
                          {c.full_name || c.username}
                          {isAdminUser && (
                            <button type="button" onClick={() => unassignCoordinator(c.EmployeeId)} title="Remove coordinator"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0, lineHeight: 1, fontSize: 15 }}>×</button>
                          )}
                        </span>
                      ))}
                      {isAdminUser && (
                        <select defaultValue="" onChange={(e) => { assignCoordinator(e.target.value); e.target.value = ''; }}
                          style={{ fontSize: 12, padding: '3px 8px', borderRadius: 6, border: '1px solid var(--border-subtle, #d1d5db)', color: 'var(--fg-2)', background: 'var(--bg-surface, #fff)' }}>
                          <option value="" disabled>+ Add coordinator…</option>
                          {coordinatorDirectory
                            .filter((d) => !unitCoordinators.some((uc) => String(uc.EmployeeId) === String(d.EmployeeId)))
                            .map((d) => <option key={d.EmployeeId} value={d.EmployeeId}>{d.full_name || d.username}</option>)}
                        </select>
                      )}
                    </div>
                  </td>
                </tr>
                <EditableTableRow label="CBA Start" value={editData.CBA_START || ""} name="CBA_START" onChange={handleInputChange} isEditMode={isEditMode} isDate={true} disabled={!canEditField('CBA_START')} />
                <EditableTableRow label="CBA Exp" value={editData.CBA_EXP || ""} name="CBA_EXP" onChange={handleInputChange} isEditMode={isEditMode} isDate={true} disabled={!canEditField('CBA_EXP')} />
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <div className="card-head"><Briefcase size={15} style={{ color: 'var(--fg-3)' }} /><h3>Payroll & HR</h3></div>
          <div style={{ padding: 0 }}>
            <table className="tbl kv">
              <tbody>
                {payrollContacts.map((contact, index) => (
                  <React.Fragment key={`payroll-contact-${index}`}>
                    {payrollContacts.length > 1 && (
                      <tr><td colSpan={2} style={{ fontWeight: 600, padding: '8px 16px', fontSize: 12.5, color: 'var(--fg-3)' }}>Contact {index + 1}</td></tr>
                    )}
                    <EditableTableRow label="Type" value={PAYROLL_CONTACT_TYPE_OPTIONS.find(o => o.value === (contact.contactType || "sharefile"))?.label || ""} name={`PayrollHRContactType-${index}`} onChange={e => handlePayrollContactChange(index, "contactType", e.target.idValue || e.target.value || "sharefile")} isEditMode={isEditMode} isDropdown={true} options={PAYROLL_CONTACT_TYPE_OPTIONS.map(o => ({ id: o.value, employee_full_name: o.label }))} disabled={!canEditPayrollContacts} />
                    <EditableTableRow label="Title" value={contact.contactTitle || ""} name={`PayrollHRContactTitle-${index}`} onChange={e => handlePayrollContactChange(index, "contactTitle", e.target.value)} isEditMode={isEditMode} disabled={!canEditPayrollContacts} />
                    <EditableTableRow label="Name" value={contact.contactName || ""} name={`PayrollHRContactName-${index}`} onChange={e => handlePayrollContactChange(index, "contactName", e.target.value)} isEditMode={isEditMode} disabled={!canEditPayrollContacts} />
                    <EditableTableRow label="Email" value={contact.contactEmail || ""} name={`PayrollHRContactEmail-${index}`} onChange={e => handlePayrollContactChange(index, "contactEmail", e.target.value)} isEditMode={isEditMode} disabled={!canEditPayrollContacts} />
                    <EditableTableRow label="Phone" value={contact.contactPhone || ""} name={`PayrollHRContactNumber-${index}`} onChange={e => handlePayrollContactChange(index, "contactPhone", e.target.value)} isEditMode={isEditMode} disabled={!canEditPayrollContacts} />
                    {isEditMode && canEditPayrollContacts && payrollContacts.length > 1 && (
                      <tr><td colSpan={2} style={{ padding: '4px 16px' }}>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => handleRemovePayrollContact(index)}>Remove</button>
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            {isEditMode && canEditPayrollContacts && (
              <div style={{ padding: '10px 16px' }}>
                <button type="button" className="btn btn-secondary btn-sm" onClick={handleAddPayrollContact}>+ Add Contact</button>
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-head"><FileText size={15} style={{ color: 'var(--fg-3)' }} /><h3>Contract & Negotiations</h3></div>
          <div style={{ padding: 0 }}>
            <table className="tbl kv">
              <tbody>
                <EditableTableRow label="New Recommended Language" value={editData.NewRecommendedContractLanguage || ""} name="NewRecommendedContractLanguage" onChange={handleInputChange} isEditMode={isEditMode} disabled={!canEditField('NewRecommendedContractLanguage')} />
                <EditableTableRow label="Orientations Happening?" value={editData.AreOrientationsHappening || ""} name="AreOrientationsHappening" onChange={handleInputChange} isEditMode={isEditMode} disabled={!canEditField('AreOrientationsHappening')} />
                <EditableTableRow label="Who Does Orientation" value={editData.WhoDoesOrientation || ""} name="WhoDoesOrientation" onChange={handleInputChange} isEditMode={isEditMode} disabled={!canEditField('WhoDoesOrientation')} />
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Raises / GWI (full width) */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-head"><DollarSign size={15} style={{ color: 'var(--fg-3)' }} /><h3>Raises / GWI History</h3></div>
        <div className="card-body">
          {isEditMode && (
            <form onSubmit={handleRaiseSubmit} style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end', padding: '12px 16px', background: 'var(--bg-sunken)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-subtle)' }}>
              <div className="dh-field" style={{ margin: 0 }}>
                <label className="dh-label">Effective Date *</label>
                <input type="date" required className="dh-input" value={raiseForm.effective_date} onChange={e => setRaiseForm(f => ({ ...f, effective_date: e.target.value }))} />
              </div>
              <div className="dh-field" style={{ margin: 0 }}>
                <label className="dh-label">Type *</label>
                <select className="dh-select" value={raiseForm.raise_type} onChange={e => setRaiseForm(f => ({ ...f, raise_type: e.target.value }))}>
                  <option value="GWI">GWI</option>
                  <option value="Step">Step</option>
                  <option value="COLA">COLA</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="dh-field" style={{ margin: 0 }}>
                <label className="dh-label">% Raise</label>
                <input type="number" step="0.01" placeholder="2.50" className="dh-input" style={{ width: 80 }} value={raiseForm.raise_pct} onChange={e => setRaiseForm(f => ({ ...f, raise_pct: e.target.value }))} />
              </div>
              <div className="dh-field" style={{ margin: 0 }}>
                <label className="dh-label">$ Amount</label>
                <input type="number" step="0.01" placeholder="500.00" className="dh-input" style={{ width: 90 }} value={raiseForm.raise_amt} onChange={e => setRaiseForm(f => ({ ...f, raise_amt: e.target.value }))} />
              </div>
              <div className="dh-field" style={{ margin: 0, flex: 1, minWidth: 140 }}>
                <label className="dh-label">Notes</label>
                <input type="text" placeholder="Optional notes" className="dh-input" value={raiseForm.notes} onChange={e => setRaiseForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
              <button type="submit" disabled={raiseSaving} className="btn btn-primary btn-sm">{raiseSaving ? 'Adding…' : '+ Add Raise'}</button>
              {raiseError && <div role="alert" style={{ color: 'var(--color-danger)', fontSize: 13, marginTop: 8, flexBasis: '100%' }}>{raiseError}</div>}
            </form>
          )}
          {raises.length === 0 ? (
            <p style={{ color: 'var(--fg-3)', fontSize: 13.5, margin: 0 }}>No raise history recorded.</p>
          ) : (() => {
            const byYear = raises.reduce((acc, r) => {
              const yr = r.effective_date ? raiseYear(r.effective_date) : 'Unknown';
              if (!acc[yr]) acc[yr] = [];
              acc[yr].push(r);
              return acc;
            }, {});
            const years = Object.keys(byYear).sort((a, b) => b - a);
            return (
              <div className="table-wrap">
                <table className="tbl">
                  <thead>
                    <tr><th>Date</th><th>Type</th><th>%</th><th>$</th><th>Notes</th><th>By</th>{isEditMode && <th></th>}</tr>
                  </thead>
                  <tbody>
                    {years.map(yr => (
                      <>
                        <tr key={`yr-${yr}`}>
                          <td className="card-title" colSpan={isEditMode ? 7 : 6} style={{ padding: '8px 16px 4px', fontWeight: 700, fontSize: 12, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.06em', background: 'var(--bg-sunken)', borderBottom: '1px solid var(--border-subtle)' }}>{yr}</td>
                        </tr>
                        {byYear[yr].map(r => (
                          <tr key={r.id}>
                            <td className="card-title" style={{ whiteSpace: 'nowrap' }}>{fmtRaiseDate(r.effective_date)}</td>
                            <td data-label="Type"><span style={{ display: 'inline-flex', padding: '2px 8px', borderRadius: 999, background: 'var(--bg-accent-soft)', color: 'var(--accent)', fontSize: 12, fontWeight: 600 }}>{r.raise_type}</span></td>
                            <td data-label="% Raise" style={{ fontWeight: 600, color: 'var(--color-success)' }}>{r.raise_pct != null ? `${r.raise_pct}%` : '—'}</td>
                            <td data-label="$ Amount" style={{ fontWeight: 600, color: 'var(--color-success)' }}>{r.raise_amt != null ? `$${Number(r.raise_amt).toLocaleString()}` : '—'}</td>
                            <td data-label="Notes">{r.notes || '—'}</td>
                            <td data-label="By" style={{ color: 'var(--fg-3)', fontSize: 12.5 }}>{r.created_by || '—'}</td>
                            {isEditMode && <td data-label="Delete"><button onClick={() => handleRaiseDelete(r.id)} style={{ background: 'none', border: 'none', color: 'var(--color-danger)', cursor: 'pointer', fontSize: 14 }}>✕</button></td>}
                          </tr>
                        ))}
                      </>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Row 3: Metrics | Audits | Financial + Elections */}
      <div className="grid-3" style={{ marginBottom: 20 }}>
        <div className="card">
          <div className="card-head"><BarChart3 size={15} style={{ color: 'var(--fg-3)' }} /><h3>Membership Metrics</h3></div>
          <div style={{ padding: 0 }}>
            <table className="tbl kv">
              <tbody>
                <EditableTableRow label="Active Job Count" value={editData.ActiveJobCount || ""} name="ActiveJobCount" advanceOnEnter onChange={handleInputChange} isEditMode={isEditMode} disabled={!canEditField('ActiveJobCount')} />
                <EditableTableRow label="Total Dues Payers (editable; auto-fills from card audit when blank)" value={editData.TotalDuesPayers || ""} name="TotalDuesPayers" advanceOnEnter onChange={handleInputChange} isEditMode={isEditMode} disabled={!canEditField('TotalDuesPayers')} />
                <tr>
                  <td style={{ textAlign: "left" }}>% Paying Dues:</td>
                  <td style={{ textAlign: "right", fontWeight: "bold" }}>
                    {(() => {
                      const total = Number(editData.ActiveJobCount);
                      const paying = Number(editData.TotalDuesPayers);
                      if (!Number.isFinite(total) || total <= 0 || !Number.isFinite(paying) || String(editData.TotalDuesPayers ?? '').trim() === '') return 'No data';
                      return `${Math.round((paying / total) * 100)}%`;
                    })()}
                  </td>
                </tr>
                <EditableTableRow label="On Centralized Dues?" value={editData.OnCentralizedDues || ""} name="OnCentralizedDues" onChange={handleInputChange} isEditMode={isEditMode} disabled={!canEditField('OnCentralizedDues')} />
                <EditableTableRow label="Dues Month Applied" value={editData.DuesMonthApplied || ""} name="DuesMonthApplied" onChange={handleInputChange} isEditMode={isEditMode} disabled={!canEditField('DuesMonthApplied')} />
                <EditableTableRow label="Dues Frequency" value={editData.DuesFrequency || ""} name="DuesFrequency" onChange={handleInputChange} isEditMode={isEditMode} disabled={!canEditField('DuesFrequency')} />
                <EditableTableRow label="Home Emails" value={editData.NumberOfHomeEmails || ""} name="NumberOfHomeEmails" advanceOnEnter onChange={handleInputChange} isEditMode={isEditMode} disabled={!canEditField('NumberOfHomeEmails')} />
                <EditableTableRow label="Phone Numbers" value={editData.NumberOfPhoneNumbers || ""} name="NumberOfPhoneNumbers" advanceOnEnter onChange={handleInputChange} isEditMode={isEditMode} disabled={!canEditField('NumberOfPhoneNumbers')} />
                <EditableTableRow label="Last Barg Unit List" value={editData.DateLastBargUnitListReceived || ""} name="DateLastBargUnitListReceived" onChange={handleInputChange} isEditMode={isEditMode} isDate={true} disabled={!canEditField('DateLastBargUnitListReceived')} />
                <EditableTableRow label="Audit Complete" value={editData.AuditCompleteOverview || ""} name="AuditCompleteOverview" onChange={handleInputChange} isEditMode={isEditMode} isDate={true} disabled={!canEditField('AuditCompleteOverview')} />
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <div className="card-head" style={{ justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <CheckSquare size={15} style={{ color: 'var(--fg-3)' }} />
              <h3>Audits & Compliance</h3>
            </div>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowMembershipAuditModal(true)}>History</button>
          </div>
          <div style={{ padding: 0 }}>
            <table className="tbl kv">
              <tbody>
                <tr><td colSpan={2} style={{ padding: '8px 16px 4px', fontWeight: 600, fontSize: 12, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Needs Card</td></tr>
                <EditableTableRow label="No Card No Dues" value={editData.NoCardNoDues || ""} name="NoCardNoDues" advanceOnEnter onChange={handleInputChange} isEditMode={isEditMode} disabled={!canEditField('NoCardNoDues')} />
                <EditableTableRow label="Dues w/ No Card" value={editData.DuesNoCard || ""} name="DuesNoCard" advanceOnEnter onChange={handleInputChange} isEditMode={isEditMode} disabled={!canEditField('DuesNoCard')} />
                <tr><td colSpan={2} style={{ padding: '8px 16px 4px', fontWeight: 600, fontSize: 12, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Others</td></tr>
                <EditableTableRow label="Opt Out" value={editData.TotalOPTOUTS || ""} name="TotalOPTOUTS" advanceOnEnter onChange={handleInputChange} isEditMode={isEditMode} disabled={!canEditField('TotalOPTOUTS')} />
                <EditableTableRow label="Refusal" value={editData.Refusals || ""} name="Refusals" advanceOnEnter onChange={handleInputChange} isEditMode={isEditMode} disabled={!canEditField('Refusals')} />
                <EditableTableRow label="Card No Dues" value={editData.CardNoDues || ""} name="CardNoDues" advanceOnEnter onChange={handleInputChange} isEditMode={isEditMode} disabled={!canEditField('CardNoDues')} />
                <EditableTableRow label="Dues w/ Card" value={editData.DuesWithCard || ""} name="DuesWithCard" advanceOnEnter onChange={handleInputChange} isEditMode={isEditMode} disabled={!canEditField('DuesWithCard')} />
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card">
            <div className="card-head"><DollarSign size={15} style={{ color: 'var(--fg-3)' }} /><h3>Financial Metrics</h3></div>
            <div style={{ padding: 0 }}>
              <table className="tbl kv">
                <tbody>
                  <EditableTableRow label="EIN" value={editData.ein || ""} name="ein" onChange={handleInputChange} isEditMode={isEditMode} disabled={!canEditField('ein')} />
                  <EditableTableRow label="PEOPLE Contributors" value={editData.PEOPLEContributors || ""} name="PEOPLEContributors" onChange={handleInputChange} isEditMode={isEditMode} disabled={!canEditField('PEOPLEContributors')} />
                  <EditableTableRow label="Recommits/Saves" value={editData.RecommitsSaves || ""} name="RecommitsSaves" onChange={handleInputChange} isEditMode={isEditMode} disabled={!canEditField('RecommitsSaves')} />
                </tbody>
              </table>
            </div>
          </div>
          <div className="card">
            <div className="card-head"><Vote size={15} style={{ color: 'var(--fg-3)' }} /><h3>Elections</h3></div>
            <div style={{ padding: 0 }}>
              <table className="tbl kv">
                <tbody>
                  <EditableTableRow label="Last Officer Update" value={editData.DateLastOfficerUpdate || ""} name="DateLastOfficerUpdate" onChange={handleInputChange} isEditMode={isEditMode} isDate={true} disabled={!canEditField('DateLastOfficerUpdate')} />
                  <EditableTableRow label="Last Election" value={editData.LastElection || ""} name="LastElection" onChange={handleInputChange} isEditMode={isEditMode} isDate={true} disabled={!canEditField('LastElection')} />
                  <EditableTableRow label="Next Election (IU)" value={editData.IUMonthYearNextElection || ""} name="IUMonthYearNextElection" onChange={handleInputChange} isEditMode={isEditMode} isDate={true} disabled={!canEditField('IUMonthYearNextElection')} />
                  <EditableTableRow label="Length of Term" value={editData.LengthOfTerm || ""} name="LengthOfTerm" onChange={handleInputChange} isEditMode={isEditMode} disabled={!canEditField('LengthOfTerm')} />
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Notes */}
      <NotesPanel bargainingUnitId={id} isEditMode={isEditMode} />

      {/* Leadership */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-head"><Award size={15} style={{ color: 'var(--fg-3)' }} /><h3>Leadership</h3></div>
        <div className="card-body">
          <OfficersPanel ref={officersPanelRef} unitId={id} isEditMode={isEditMode} />
        </div>
      </div>

      {/* Documents */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-head"><FileText size={15} style={{ color: 'var(--fg-3)' }} /><h3>Contract Documents</h3></div>
        <div className="card-body">
          <DocumentManager
            unitId={id}
            unitName={editData.NAME || bargainingUnits[0]?.NAME || 'Unknown Unit'}
            unitLocal={editData.LOCAL || bargainingUnits[0]?.LOCAL || ''}
          />
        </div>
      </div>

      {showAuditLog && (
        <AuditLogModal unitId={id} onClose={() => setShowAuditLog(false)} />
      )}
      {showMembershipAuditModal && (
        <MembershipAuditModal
          unitId={id}
          localLabel={`${editData.LOCAL || ''}-${editData.UNIT ? String(editData.UNIT).padStart(4, '0') : '0000'} ${editData.BARGAINING_UNIT || editData.NAME || ''}`.trim()}
          onClose={() => setShowMembershipAuditModal(false)}
        />
      )}
    </div>
  );
}
export default BargainingUnitInfo;
